import { FC, KeyboardEvent, useEffect, useMemo, useState } from "react";

import { Icon } from "@/components";
import {
    Button,
    Heading,
    IconButton,
    Input,
    ProgressCircular,
    Table,
    Text,
    type TableColumn,
} from "@/ui";
import { LineItem } from "@/types";

import { useCheckBarcodes, useUpdateBarcodes } from "./hooks";
import styles from "./ProductBarcodes.module.scss";

type VerifiedStatus = "error" | "success" | "not-verified";

type IBarcodesByProduct = {
    [key in number]: {
        barcodes: string[];
        verifiedStatus: VerifiedStatus;
        errorMessage?: string;
    };
};

type BarcodeRow = {
    index: number;
    value: string;
};

export type ProductBarcodesViewProps = {
    lineItems: LineItem[];
    activeProductIndex: number;
    barcodesByProduct: IBarcodesByProduct;
    currentBarcode: string;
    isChecking?: boolean;
    isUpdating?: boolean;
    onPrev: () => void;
    onNext: () => void;
    onBarcodeChange: (value: string) => void;
    onBarcodeSubmit: (value: string) => void;
    onRemoveBarcode: (index: number) => void;
    onUpdateBarcodes: () => void;
};

const ChevronLeftIcon = () => <Icon icon="arrow_left" />;
const ChevronRightIcon = () => <Icon icon="arrow_right" />;
const TrashIcon = () => <Icon icon="delete" />;

/**
 * Presentational barcode scanner UI (DS).
 * Connected `ProductBarcodes` wires GraphQL check/update hooks.
 */
export const ProductBarcodesView: FC<ProductBarcodesViewProps> = ({
    lineItems,
    activeProductIndex,
    barcodesByProduct,
    currentBarcode,
    isChecking = false,
    isUpdating = false,
    onPrev,
    onNext,
    onBarcodeChange,
    onBarcodeSubmit,
    onRemoveBarcode,
    onUpdateBarcodes,
}) => {
    const productsSize = lineItems?.length ?? 0;
    const activeProduct = lineItems?.[activeProductIndex];
    const displayIndex = activeProductIndex + 1;
    const scannedBarcodes =
        barcodesByProduct[activeProductIndex]?.barcodes ?? [];
    const activeVerifiedStatus =
        barcodesByProduct[activeProductIndex]?.verifiedStatus;
    const errorMessage = barcodesByProduct[activeProductIndex]?.errorMessage;

    const readyToVerify =
        Boolean(scannedBarcodes.length) &&
        Boolean(activeProduct) &&
        scannedBarcodes.length === activeProduct?.quantity;

    const allowUpdateBarcodes =
        Object.entries(barcodesByProduct).length === lineItems?.length &&
        Object.entries(barcodesByProduct)
            .map(([, item]) => item)
            .every(({ verifiedStatus }) => verifiedStatus === "success");

    const rows: BarcodeRow[] = scannedBarcodes.map((value, index) => ({
        index,
        value,
    }));

    const columns: TableColumn<BarcodeRow>[] = [
        {
            id: "actions",
            header: "",
            align: "left",
            width: 56,
            minWidth: 56,
            renderCell: row => (
                <IconButton
                    aria-label={`Remove barcode ${row.value}`}
                    disabled={isChecking}
                    onClick={() => onRemoveBarcode(row.index)}
                    size="sm"
                    type="button"
                    variant="tertiary"
                >
                    <TrashIcon />
                </IconButton>
            ),
        },
        {
            id: "value",
            header: "",
            renderCell: row => (
                <Text as="span" variant="body-md">
                    {row.value}
                </Text>
            ),
        },
    ];

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== "Enter") {
            return;
        }
        event.preventDefault();
        const value = event.currentTarget.value.trim();
        if (!value) {
            return;
        }
        onBarcodeSubmit(value);
    };

    return (
        <section className={styles.root}>
            <Heading className={styles.title} level={5}>
                Scan products barcode
            </Heading>

            <div className={styles.header}>
                <div className={styles.headerMain}>
                    <div className={styles.slider}>
                        <div className={styles.sliderNav}>
                            <IconButton
                                aria-label="Previous product"
                                disabled={activeProductIndex === 0}
                                onClick={onPrev}
                                size="sm"
                                type="button"
                                variant="tertiary"
                            >
                                <ChevronLeftIcon />
                            </IconButton>
                            <span className={styles.counter}>
                                {`${displayIndex}/${productsSize}`}
                            </span>
                            <IconButton
                                aria-label="Next product"
                                disabled={
                                    activeProductIndex >= productsSize - 1 ||
                                    productsSize === 0
                                }
                                onClick={onNext}
                                size="sm"
                                type="button"
                                variant="tertiary"
                            >
                                <ChevronRightIcon />
                            </IconButton>
                        </div>
                        <Text as="p" className={styles.productName}>
                            {activeProduct?.name || "—"}
                        </Text>
                    </div>

                    <div className={styles.meta}>
                        <Text as="p" className={styles.scanned}>
                            {`${scannedBarcodes.length}/${
                                activeProduct?.quantity ?? 0
                            } scanned`}
                        </Text>
                        {isChecking ? (
                            <span
                                className={styles.verification}
                                data-tone="info"
                            >
                                <ProgressCircular
                                    aria-label="Verification in progress"
                                    className={styles.verificationProgress}
                                    indeterminate
                                    size="sm"
                                />
                                Verification in progress
                            </span>
                        ) : null}
                        {activeVerifiedStatus === "success" && !isChecking ? (
                            <span
                                className={styles.verification}
                                data-tone="success"
                            >
                                Verification completed
                            </span>
                        ) : null}
                        {activeVerifiedStatus === "error" && !isChecking ? (
                            <span
                                className={styles.verification}
                                data-tone="error"
                            >
                                {errorMessage || "Verification failed"}
                            </span>
                        ) : null}
                    </div>
                </div>

                <Button
                    className={styles.updateBtn}
                    disabled={!allowUpdateBarcodes}
                    loading={isUpdating}
                    onClick={onUpdateBarcodes}
                    type="button"
                    variant="primary"
                >
                    Update Barcode
                </Button>
            </div>

            <div className={styles.scanField}>
                <Input
                    density="compact"
                    disabled={readyToVerify}
                    fullWidth
                    label="Scan barcode"
                    labelBehavior="inline"
                    onChange={event => onBarcodeChange(event.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Scan barcode"
                    value={currentBarcode}
                />
            </div>

            {rows.length ? (
                <div className={styles.tableBlock}>
                    <p className={styles.tableTitle}>Barcode</p>
                    <div className={styles.tableWrap}>
                        <Table
                            columns={columns}
                            emptyMessage="No barcodes scanned"
                            getRowId={row => String(row.index)}
                            rows={rows}
                            scrollMaxHeight={160}
                            showEmptyGraphic={false}
                        />
                    </div>
                </div>
            ) : null}
        </section>
    );
};

interface ProductBarcodesProps {
    orderId: string;
    lineItems: LineItem[];
    isLoading: boolean;
}

const ProductBarcodes: FC<ProductBarcodesProps> = ({
    lineItems,
    isLoading,
    orderId,
}) => {
    const productsSize = lineItems?.length ?? 0;
    const [activeProductIndex, setActiveProductIndex] = useState(0);
    const activeProduct = useMemo(
        () => lineItems?.[activeProductIndex],
        [activeProductIndex, lineItems],
    );
    const [barcodesByProduct, setBarcodesByProduct] =
        useState<IBarcodesByProduct>({});
    const scannedBarcodes = useMemo(
        () => barcodesByProduct[activeProductIndex]?.barcodes,
        [barcodesByProduct, activeProductIndex],
    );
    const [currentBarcode, setCurrentBarcode] = useState("");
    const readyToVerify = useMemo(() => {
        if (!scannedBarcodes?.length || !activeProduct) {
            return false;
        }
        return scannedBarcodes?.length === activeProduct?.quantity;
    }, [scannedBarcodes, activeProduct]);

    const { checkLineItemBarcodes, isChecking } = useCheckBarcodes({
        onCompleted: itemIndex => {
            setBarcodesByProduct(barcodes => ({
                ...barcodes,
                [itemIndex]: {
                    ...barcodes[itemIndex],
                    verifiedStatus: "success",
                },
            }));
        },
        onError: (itemIndex, errorMessage) => {
            setBarcodesByProduct(barcodes => ({
                ...barcodes,
                [itemIndex]: {
                    ...barcodes[itemIndex],
                    verifiedStatus: "error",
                    errorMessage,
                },
            }));
        },
    });

    const { updateLineItemBarcodes, isUpdating } = useUpdateBarcodes(orderId);

    const handleUpdateBarcodes = () => {
        updateLineItemBarcodes(
            orderId,
            lineItems.map((item, index) => ({
                ...item,
                barcodes: barcodesByProduct[index].barcodes,
            })),
        );
    };

    const handlePrev = () => {
        setActiveProductIndex(index => Math.max(index - 1, 0));
    };

    const handleNext = () => {
        setActiveProductIndex(index => Math.min(index + 1, productsSize - 1));
    };

    const handleBarcodeSubmit = (value: string) => {
        setBarcodesByProduct(barcodes => ({
            ...barcodes,
            [activeProductIndex]: {
                barcodes: [
                    ...(barcodes[activeProductIndex]?.barcodes ?? []),
                    value,
                ],
                verifiedStatus:
                    barcodes[activeProductIndex]?.verifiedStatus ??
                    "not-verified",
            },
        }));
        setCurrentBarcode("");
    };

    const handleRemoveBarCode = (index: number) => {
        setBarcodesByProduct(barcodes => ({
            ...barcodes,
            [activeProductIndex]: {
                barcodes: (barcodes[activeProductIndex]?.barcodes ?? []).filter(
                    (_, pos) => pos !== index,
                ),
                verifiedStatus: "not-verified",
            },
        }));
    };

    useEffect(() => {
        if (
            readyToVerify &&
            barcodesByProduct[activeProductIndex]?.verifiedStatus ===
                "not-verified"
        ) {
            checkLineItemBarcodes(
                orderId,
                activeProduct?.id,
                activeProductIndex,
                scannedBarcodes,
                activeProduct?.variants?.[0]?.type,
            );
        }
    }, [readyToVerify]);

    useEffect(() => {
        setBarcodesByProduct(barcodesFromLineItems(lineItems));
        setActiveProductIndex(0);
        setCurrentBarcode("");
    }, [lineItems]);

    if (isLoading) {
        return null;
    }

    return (
        <ProductBarcodesView
            activeProductIndex={activeProductIndex}
            barcodesByProduct={barcodesByProduct}
            currentBarcode={currentBarcode}
            isChecking={isChecking}
            isUpdating={isUpdating}
            lineItems={lineItems}
            onBarcodeChange={setCurrentBarcode}
            onBarcodeSubmit={handleBarcodeSubmit}
            onNext={handleNext}
            onPrev={handlePrev}
            onRemoveBarcode={handleRemoveBarCode}
            onUpdateBarcodes={handleUpdateBarcodes}
        />
    );
};

const barcodesFromLineItems = (lineItems: LineItem[]): IBarcodesByProduct =>
    (lineItems?.reduce(
        (acc, item, index) => ({
            ...acc,
            [index]: {
                barcodes: (item.barcodes ?? []).filter(Boolean) as string[],
                verifiedStatus: item.barcodes?.length
                    ? "success"
                    : "not-verified",
            },
        }),
        {},
    ) ?? {}) as IBarcodesByProduct;

export { ProductBarcodes, barcodesFromLineItems };
export type { IBarcodesByProduct, VerifiedStatus };
